import {
    deleteOrganizationById,
    getAllOrganizations,
    IOrganization,
} from "@/api";
import { AdminPageHeader, SearchField } from "@/components/admin";
import { Button } from "@/components/shared/buttons/Button";
import { DestructiveDialog } from "@/components/shared/dialogs/destructive-dialog/DestructiveDialog";
import { Table } from "@/components/shared/table/Table";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { formatDate, handleRowSelection } from "@/utils";
import {
    GridColDef,
    GridRowParams,
    GridRowSelectionModel,
    GridSortModel,
} from "@mui/x-data-grid-pro";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDialogs } from "@toolpad/core/useDialogs";
import { useEffect, useMemo, useRef, useState } from "react";
import { CreateOrganizationDialog } from "../dialogs/organization/CreateOrganizationDialog";
import { OrganizationName } from "../OrganizationName";
import { DeleteOrganizationButton } from "./DeleteOrganizationButton";
import { EditOrganizationButton } from "./EditOrganizationButton";
import { OrganizationGo } from "./OrganizationGo";

const columns: GridColDef<IOrganization>[] = [
    {
        field: "name",
        headerName: "Name",
        display: "flex",
        renderCell: ({ row }) => <OrganizationName organization={row} />,
    },
    {
        field: "created_at",
        headerName: "Date Created",
        type: "date",
        display: "flex",
        valueFormatter: (value) => {
            return formatDate(value);
        },
    },
    {
        field: "group",
        headerName: "Group",
        display: "flex",
        valueGetter: (_, row) => row.group?.name ?? "-",
    },
    {
        field: "actions",
        headerName: "Actions",
        display: "flex",
        type: "actions",
        getActions: ({ row }: GridRowParams<IOrganization>) => [
            <EditOrganizationButton organization={row} />,
            <DeleteOrganizationButton organizationId={row.id} />,
        ],
    },
    {
        field: "go",
        display: "flex",
        type: "actions",
        renderCell: ({ row }) => <OrganizationGo organizationId={row.id} />,
    },
];

export const OrganizationsTable = () => {
    const dialogs = useDialogs();
    const queryClient = useQueryClient();

    const handleDeleteSelected = async () => {
        await dialogs.open(DestructiveDialog, {
            itemIds: selectedIds,
            subject: "organizations",
            mutationFn: deleteOrganizationById,
            onSucceeded: () => {
                queryClient.invalidateQueries({
                    queryKey: ["admin-organizations"],
                });
            },
        });
    };

    const handleNewOrganization = async () => {
        await dialogs.open(CreateOrganizationDialog);
    };

    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    const handleRowSelectionModelChange = (
        rowSelectionModel: GridRowSelectionModel
    ) => {
        setSelectedIds(handleRowSelection(rowSelectionModel, rows));
    };

    const {
        search,
        debouncedSearch,
        setSearch,
        clear: handleSearchClear,
    } = useDebouncedSearch();

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });
    const [sortModel, setSortModel] = useState<GridSortModel>([]);

    const [rows, setRows] = useState<IOrganization[]>([]);

    const autoSizeOptions = {
        columns: ["name", "created_at", "group", "actions"],
        includeHeaders: true,
        includeOutliers: true,
        expand: true,
    };

    // Reset to page 0 when search or sort changes, so a narrowed result set
    // never leaves the grid stuck on a page past the new total.
    useEffect(() => {
        setPaginationModel((prev) =>
            prev.page === 0 ? prev : { ...prev, page: 0 }
        );
    }, [debouncedSearch, sortModel]);

    const sortFilter =
        sortModel.length > 0
            ? { sort_by: sortModel[0].field, order: sortModel[0].sort ?? "asc" }
            : {};

    const { data, isLoading } = useQuery({
        queryKey: [
            "admin-organizations",
            paginationModel.page,
            paginationModel.pageSize,
            debouncedSearch,
            sortModel,
        ],
        queryFn: () =>
            getAllOrganizations(
                paginationModel.page + 1,
                paginationModel.pageSize,
                sortFilter,
                debouncedSearch || null
            ),
    });

    useEffect(() => {
        if (data) {
            setRows(data.items);
        }
    }, [data]);

    const rowCountRef = useRef(data?.total || 0);

    const rowCount = useMemo(() => {
        if (data?.total !== undefined) {
            rowCountRef.current = data?.total;
        }
        return rowCountRef.current;
    }, [data?.total]);

    return (
        <>
            <AdminPageHeader
                title="Organizations"
                subtitle="Manage organization settings, users, and checklists"
            >
                <Button
                    variant="contained"
                    color="secondary"
                    disableElevation
                    sx={{ borderRadius: 22 }}
                    onClick={handleNewOrganization}
                >
                    Add New Organization
                </Button>

                {selectedIds.length > 0 && (
                    <Button
                        variant="contained"
                        color="tertiary"
                        disableElevation
                        sx={{ borderRadius: 22, ml: 1 }}
                        onClick={handleDeleteSelected}
                    >
                        Delete Selected
                    </Button>
                )}
            </AdminPageHeader>
            <div className="mt-6 mb-4">
                <SearchField
                    value={search}
                    onChange={setSearch}
                    onClear={handleSearchClear}
                    placeholder="Search organizations..."
                />
            </div>
            <div>
                <Table
                    loading={isLoading}
                    columns={columns}
                    rows={rows}
                    rowCount={rowCount}
                    checkboxSelection
                    onRowSelectionModelChange={handleRowSelectionModelChange}
                    disableColumnResize
                    disableColumnFilter
                    autoSize
                    autosizeOptions={autoSizeOptions}
                    paginationMode="server"
                    paginationModel={paginationModel}
                    pageSizeOptions={[10, 25, 50, 100]}
                    sortingMode="server"
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                    onPaginationModelChange={setPaginationModel}
                />
            </div>
        </>
    );
};
